import {
  Button,
  Checkbox,
  Loader,
  Menu,
  Modal,
  Progress,
  Select,
  TextInput,
} from '@mantine/core';
import {
  joinNames,
  MANUAL_TEMPLATE_KEYS,
  MAIL_TEMPLATES,
  type ManualTemplateKey,
} from '@reditelji/shared';
import { useEffect, useMemo, useState } from 'react';
import { useAllShifts } from '../api/posts';
import {
  useMailBatch,
  useMailMutations,
  useRecentBatches,
  type MailBatch,
  type MailSendState,
} from '../api/mail';
import { useVolunteers, type VolunteerRow } from '../api/volunteers';
import {
  householdsIn,
  mailShiftCounts,
  sendStatesFor,
  shortDate,
  type SendStates,
} from './sendState';
import { bySuffix, useCoordinatorLabel } from '../lib/people';

/** The server refuses more than this in one request (mailBulkSendSchema). */
const BULK_LIMIT = 300;

type Filter = 'vsi' | ManualTemplateKey;

/**
 * People × the four mails.
 *
 * The question this answers is the one the per-person card cannot: who has been
 * missed. Filtering to "ni prejel uvodnega pisma" and sending to the selection
 * is the whole start-of-season workflow in two clicks.
 */
export function RecipientMatrix({
  sends,
  onCompose,
}: {
  sends: MailSendState[];
  /** Write an obvestilo to the selection instead of one of the four mails. */
  onCompose: (ids: string[]) => void;
}) {
  const volunteers = useVolunteers();
  const shifts = useAllShifts();
  const [query, setQuery] = useState('');
  const [missing, setMissing] = useState<Filter>('vsi');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState<ManualTemplateKey | null>(null);

  const shiftCounts = useMemo(() => mailShiftCounts(shifts.data), [shifts.data]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (volunteers.data ?? [])
      .filter((v) => v.active)
      .map((v) => ({ v, states: sendStatesFor(v, sends, shiftCounts.get(v.id) ?? 0) }))
      .filter(({ v }) => q === '' || v.name.toLowerCase().includes(q))
      .filter(({ states }) => missing === 'vsi' || states[missing].sentAt === null)
      .sort((a, b) => a.v.name.localeCompare(b.v.name, 'sl'));
  }, [volunteers.data, sends, shiftCounts, query, missing]);

  const toggle = (id: string) =>
    setPicked((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  /*
   * The selection follows what is on screen.
   *
   * Without this, ticking 40 people under "Ni prejel: uvodno pismo" and then
   * clearing the filter leaves those 40 selected and invisible — and a bulk
   * send would reach people the coordinator can no longer see.
   */
  useEffect(() => {
    const visible = new Set(rows.map(({ v }) => v.id));
    setPicked((s) => {
      const next = new Set([...s].filter((id) => visible.has(id)));
      return next.size === s.size ? s : next;
    });
  }, [rows]);

  // "Select all" can only ever mean the first BULK_LIMIT, so past the cap it
  // stays indeterminate rather than claiming to have selected the whole list.
  const selectable = rows.slice(0, BULK_LIMIT);
  const overCap = rows.length > BULK_LIMIT;
  const allSelectable = selectable.length > 0 && selectable.every(({ v }) => picked.has(v.id));
  const allPicked = allSelectable && !overCap;

  // Both, not just the roster: with shifts still in flight every napotnica cell
  // would read "brez izmen" and then flip, and a coordinator could act on the
  // wrong state in between.
  if (volunteers.isPending || shifts.isPending) {
    return (
      <div className="ml-rc-empty">
        <Loader size="sm" />
      </div>
    );
  }

  return (
    <div className="ml-rc">
      <div className="ml-rc-tools">
        <TextInput
          size="xs"
          placeholder="Išči po imenu"
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
          w={200}
        />
        <Select
          size="xs"
          w={230}
          label={undefined}
          value={missing}
          onChange={(v) => setMissing((v ?? 'vsi'))}
          data={[
            { value: 'vsi', label: 'Vsi prostovoljci' },
            ...MANUAL_TEMPLATE_KEYS.map((k) => ({
              value: k,
              label: `Ni prejel: ${MAIL_TEMPLATES[k].label.toLowerCase()}`,
            })),
          ]}
          comboboxProps={{ withinPortal: true }}
        />
        <span className="ml-hint">{rows.length} oseb</span>
        <div style={{ flex: 1 }} />
        <RecentBatches />
      </div>

      <div className="ml-rc-scroll">
        <table className="ml-rc-table">
          <thead>
            <tr>
              <th className="ml-rc-check">
                <Checkbox
                  size="xs"
                  aria-label="Izberi vse prikazane"
                  checked={allPicked}
                  indeterminate={!allPicked && picked.size > 0}
                  // Adds to the selection rather than replacing it, so the
                  // header and the individual ticks mean the same thing.
                  onChange={() =>
                    setPicked((s) => {
                      if (allSelectable) {
                        const next = new Set(s);
                        for (const { v } of selectable) next.delete(v.id);
                        return next;
                      }
                      return new Set([...s, ...selectable.map(({ v }) => v.id)]);
                    })
                  }
                />
              </th>
              <th className="ml-rc-name">Prostovoljec</th>
              {MANUAL_TEMPLATE_KEYS.map((k) => (
                <th key={k}>{MAIL_TEMPLATES[k].label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(({ v, states }) => (
              <Row
                key={v.id}
                v={v}
                states={states}
                picked={picked.has(v.id)}
                onToggle={() => toggle(v.id)}
              />
            ))}
          </tbody>
        </table>
        {rows.length === 0 ? <div className="ml-rc-empty">Ni zadetkov.</div> : null}
      </div>

      {picked.size > 0 ? (
        <div className="ml-rc-bar">
          <span className="ml-rc-count">{picked.size} izbranih</span>
          {rows.length > BULK_LIMIT ? (
            <span className="ml-hint">
              Naenkrat gre lahko največ {BULK_LIMIT} sporočil — zoži izbor.
            </span>
          ) : null}
          <Button size="xs" variant="subtle" onClick={() => setPicked(new Set())}>
            Počisti izbor
          </Button>
          <div style={{ flex: 1 }} />
          <Button
            size="xs"
            variant="light"
            disabled={picked.size > BULK_LIMIT}
            onClick={() => onCompose([...picked])}
          >
            Lastno sporočilo…
          </Button>
          <span className="ml-hint">Pošlji izbranim:</span>
          {MANUAL_TEMPLATE_KEYS.map((k) => (
            <Button
              key={k}
              size="xs"
              variant="default"
              disabled={picked.size > BULK_LIMIT}
              onClick={() => setConfirming(k)}
            >
              {MAIL_TEMPLATES[k].label}
            </Button>
          ))}
        </div>
      ) : null}

      {confirming ? (
        <BulkConfirm
          templateKey={confirming}
          ids={[...picked]}
          names={rows.filter(({ v }) => picked.has(v.id)).map(({ v }) => v.name)}
          together={householdsIn(rows.filter(({ v }) => picked.has(v.id)).map(({ v }) => v))}
          onClose={() => setConfirming(null)}
          onSent={() => setPicked(new Set())}
        />
      ) : null}
    </div>
  );
}

/**
 * The last few bulk sends.
 *
 * A batch outlives the dialog that started it, so there has to be somewhere to
 * find one again — otherwise closing the window is indistinguishable from
 * losing the send.
 */
function RecentBatches() {
  const labelOf = useCoordinatorLabel();
  const batches = useRecentBatches(6);
  const rows = batches.data ?? [];
  if (rows.length === 0) return null;

  const running = rows.filter((b) => b.status !== 'done');
  const label = running.length > 0 ? `Pošiljam (${running.length})` : 'Zadnja pošiljanja';

  return (
    <Menu position="bottom-end" withArrow width={340}>
      <Menu.Target>
        <Button size="compact-xs" variant={running.length > 0 ? 'light' : 'subtle'}>
          {label}
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        {rows.map((b) => (
          <div key={b.id} className="ml-batchrow">
            <div className="ml-batchrow-head">
              {/* The key comes from a text column, so a retired or renamed
                  template must not take the whole view down with it. */}
              <span className="ml-batchrow-name">
                {b.templateKey === 'obvestilo'
                  ? `Obvestilo: ${b.subject ?? ''}`
                  : (MAIL_TEMPLATES[b.templateKey]?.label ?? b.templateKey)}
              </span>
              <span className="ml-batchrow-when">{shortDate(b.createdAt)}</span>
            </div>
            <div className="ml-batchrow-state">
              {b.status === 'done'
                ? `poslano ${b.counts.sent} od ${b.counts.total}${
                    b.counts.failed > 0 ? ` · ${b.counts.failed} neuspelo` : ''
                  }`
                : `v teku — ${b.counts.total - b.counts.waiting} od ${b.counts.total}`}
              {bySuffix(labelOf(b.createdById, b.createdByName, ''))}
            </div>
          </div>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}

function Row({
  v,
  states,
  picked,
  onToggle,
}: {
  v: VolunteerRow;
  states: SendStates;
  picked: boolean;
  onToggle: () => void;
}) {
  return (
    <tr className={picked ? 'ml-rc-row--picked' : undefined}>
      <td className="ml-rc-check">
        <Checkbox size="xs" aria-label={v.name} checked={picked} onChange={onToggle} />
      </td>
      <td className="ml-rc-name">
        <span className="ml-rc-person">{v.name}</span>
        {v.email ? null : <span className="ml-rc-noemail">brez e-naslova</span>}
      </td>
      {MANUAL_TEMPLATE_KEYS.map((k) => {
        const s = states[k];
        return (
          <td key={k} className="ml-rc-cell">
            {s.sentAt === null ? (
              <span
                className={`ml-rc-never${s.blocked ? ' ml-rc-never--blocked' : ''}`}
                title={s.blocked ?? 'še ni bilo poslano'}
              >
                —
              </span>
            ) : (
              <span className={s.stale ? 'ml-rc-stale' : 'ml-rc-sent'} title={s.sentBy}>
                {shortDate(s.sentAt)}
                {s.stale ? ' ⟳' : ''}
              </span>
            )}
          </td>
        );
      })}
    </tr>
  );
}

/**
 * Bulk confirmation, then the send itself.
 *
 * Three phases in one dialog: what is about to happen and to whom, the batch
 * running, and the report. The batch is a row on the server before the first
 * mail leaves, so closing this dialog loses nothing — the send finishes without
 * it and stays on the Prejemniki list.
 */
function BulkConfirm({
  templateKey,
  ids,
  names,
  together,
  onClose,
  onSent,
}: {
  templateKey: ManualTemplateKey;
  ids: string[];
  names: string[];
  /** Groups of people who will get one mail between them. */
  together: string[][];
  onClose: () => void;
  onSent: () => void;
}) {
  const { sendBulk } = useMailMutations();
  const [batchId, setBatchId] = useState<string | null>(null);
  const batch = useMailBatch(batchId);
  const def = MAIL_TEMPLATES[templateKey];

  const running = batchId !== null && batch.data?.status !== 'done';
  const report = batch.data?.status === 'done' ? batch.data : null;

  return (
    <Modal
      opened
      onClose={onClose}
      title={`Pošlji: ${def.label}`}
      centered
      size="md"
      // Closing mid-send would only stop the watching, not the sending; say so
      // rather than letting a stray click look like a cancel.
      closeOnClickOutside={!running}
    >
      {report ? (
        <BatchReport batch={report} onClose={onClose} />
      ) : running ? (
        <BatchProgress batch={batch.data ?? null} total={ids.length} onClose={onClose} />
      ) : (
        <>
          <p className="ml-bulk-lede">
            {def.lede} Sporočilo gre {ids.length} {ids.length === 1 ? 'osebi' : 'izbranim'} v
            njihovem jeziku. Kdor ga ne more prejeti — brez e-naslova, brez izmen — bo izpuščen in
            naveden po pošiljanju.
          </p>
          {/* Named, not counted: a coordinator committing 150 mails should be
              able to see whose inbox they land in. */}
          <div className="ml-eyebrow" style={{ marginTop: 14 }}>
            PREJEMNIKI
          </div>
          <div className="ml-bulk-names">{names.join(', ')}</div>
          {together.length > 0 ? (
            <>
              <div className="ml-eyebrow" style={{ marginTop: 12 }}>
                SKUPNI PREDALI
              </div>
              <div className="ml-bulk-names">
                {together.map((group) => joinNames(group, 'sl')).join(' · ')} — eno pismo na
                predal, vsak s svojo povezavo.
              </div>
            </>
          ) : null}
          <div className="ml-dialog-actions">
            <div style={{ flex: 1 }} />
            <Button variant="default" size="sm" onClick={onClose}>
              Prekliči
            </Button>
            <Button
              size="sm"
              loading={sendBulk.isPending}
              onClick={() =>
                sendBulk.mutate(
                  { key: templateKey, volunteerIds: ids },
                  {
                    onSuccess: (created) => {
                      setBatchId(created.id);
                      onSent();
                    },
                  },
                )
              }
            >
              Pošlji {ids.length}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}

export function BatchProgress({
  batch,
  total,
  onClose,
}: {
  batch: MailBatch | null;
  total: number;
  onClose: () => void;
}) {
  const done = batch ? batch.counts.total - batch.counts.waiting : 0;
  const of = batch?.counts.total ?? total;
  return (
    <>
      <div className="ml-bulk-result">
        Pošiljam… {done} od {of}
      </div>
      <Progress value={of === 0 ? 0 : (done / of) * 100} size="sm" mt={10} />
      <p className="ml-hint" style={{ marginTop: 10 }}>
        Pošiljanje teče v ozadju in se ne ustavi, če to okno zapreš. Najdeš ga med zadnjimi
        pošiljanji.
      </p>
      <div className="ml-dialog-actions">
        <div style={{ flex: 1 }} />
        <Button variant="default" size="sm" onClick={onClose}>
          Zapri
        </Button>
      </div>
    </>
  );
}

export function BatchReport({ batch, onClose }: { batch: MailBatch; onClose: () => void }) {
  return (
    <>
      <div className="ml-bulk-result">
        Poslano {batch.counts.sent} {batch.counts.sent === 1 ? 'osebi' : 'osebam'}.
      </div>
      <BatchReportLists batch={batch} />
      <div className="ml-dialog-actions">
        <div style={{ flex: 1 }} />
        <Button size="sm" onClick={onClose}>
          Zapri
        </Button>
      </div>
    </>
  );
}

/** Who went together in one mail, and who was left out or failed, with why. */
export function BatchReportLists({ batch }: { batch: MailBatch }) {
  const problems = batch.items.filter((i) => i.status !== 'sent');
  // The people of one shared mailbox, each listed once — one mail between them.
  const together = new Map<string, string[]>();
  for (const i of batch.items) {
    if (i.status !== 'sent' || i.sentWith.length === 0) continue;
    const names = [i.volunteerName, ...i.sentWith].sort((a, b) => a.localeCompare(b, 'sl'));
    together.set(names.join('|'), names);
  }
  return (
    <>
      {together.size > 0 ? (
        <>
          <div className="ml-eyebrow" style={{ marginTop: 14 }}>
            V ENEM SPOROČILU
          </div>
          <ul className="ml-bulk-skipped">
            {[...together.values()].map((names) => (
              <li key={names.join('|')}>{joinNames(names, 'sl')} — skupni predal, eno pismo</li>
            ))}
          </ul>
        </>
      ) : null}
      {problems.length > 0 ? (
        <>
          <div className="ml-eyebrow" style={{ marginTop: 14 }}>
            {batch.counts.failed > 0 ? 'IZPUŠČENI IN NEUSPELI' : 'IZPUŠČENI'}
          </div>
          <ul className="ml-bulk-skipped">
            {problems.map((i) => (
              <li key={i.volunteerId}>
                <b>{i.volunteerName || 'neznan'}</b> — {i.reason}
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </>
  );
}
